import { useEffect, useRef, useState } from "react";
import { Reveal, MaskedLine, Eyebrow } from "./Reveal";
import { LogoMark } from "./Logo";

const VIBES = [
  "Smooth, rhythmic R&B with crisp vocal layering.",
  "Moody jazz-hop beats anchored by articulate storytelling.",
  "Retro-inspired synth textures with heavy atmospheric bass.",
];

const Diamond = () => (
  <svg width="8" height="8" viewBox="0 0 10 10" aria-hidden="true" className="shrink-0 mt-1.5">
    <rect x="2.2" y="2.2" width="5.6" height="5.6" transform="rotate(45 5 5)" fill="#C18C3D" opacity="0.8" />
  </svg>
);

const Music = () => {
  const [playing, setPlaying] = useState(false);
  const armed = useRef(false);

  useEffect(() => {
    if (armed.current) return;
    armed.current = true;
    window.onSpotifyIframeApiReady = (api) => {
      const el = document.getElementById("dm-spotify-embed");
      if (!el) return;
      api.createController(
        el,
        { uri: "spotify:playlist:1AjbAvS87l5ruz2FqDjI49", width: "100%", height: 420 },
        (ctrl) => {
          ctrl.addListener("playback_update", (e) => setPlaying(!e.data.isPaused));
        }
      );
    };
    if (!document.querySelector('script[src*="embed/iframe-api"]')) {
      const s = document.createElement("script");
      s.src = "https://open.spotify.com/embed/iframe-api/v1";
      document.body.appendChild(s);
    }
  }, []);

  return (
    <section id="music" data-testid="music-section" className="relative bg-ink py-28 lg:py-40">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
        <Reveal>
          <Eyebrow index="02" label="Featured Music" />
        </Reveal>

        <div className="mt-10 max-w-3xl">
          <h2 className="font-serif font-light text-cream text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
            <MaskedLine delay={0.05}>Dani Morr</MaskedLine>
            <MaskedLine delay={0.15}>
              <em className="italic text-gold">Music</em> — on wax.
            </MaskedLine>
          </h2>
          <Reveal delay={0.2}>
            <p className="text-base lg:text-lg text-cream/60 leading-relaxed mt-6 max-w-xl">
              Lush chords, jazz-hop rhythms, and sharp poetic lyricism — vintage analog
              warmth pressed against modern hip-hop and neo-soul arrangements. Drop the
              needle and the record spins with you.
            </p>
          </Reveal>
        </div>

        <div className="mt-16 grid lg:grid-cols-12 gap-10 items-stretch">
          <Reveal delay={0.1} className="lg:col-span-5">
            <div
              data-testid="record-player"
              className="relative bg-tile border border-amber/60 clip-frame p-8 lg:p-10 h-full flex flex-col items-center justify-center"
            >
              <div className="absolute top-0 right-0 w-16 h-16 border-l border-b border-amber/40 pointer-events-none" />
              <div className="relative w-full max-w-[360px] aspect-square">
                <div className="absolute inset-0 rounded-full border border-cream/10" />
                <div
                  data-testid="vinyl-disc"
                  className={`vinyl absolute inset-3 ${playing ? "spinning" : ""}`}
                >
                  <div className="absolute inset-0 rounded-full vinyl-sheen pointer-events-none" />
                  <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[34%] aspect-square rounded-full bg-gold flex flex-col items-center justify-center gap-1.5 border-2 border-ink/40">
                    <LogoMark className="w-9 h-9" />
                    <span className="font-mono text-[8px] tracking-[0.25em] text-ink font-medium">
                      SIDE A · 33⅓
                    </span>
                  </div>
                  <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-2 h-2 rounded-full bg-ink border border-cream/40" />
                </div>
                <div
                  data-testid="tonearm"
                  className={`tonearm absolute -top-1 right-1 h-[58%] w-[6px] ${playing ? "engaged" : ""}`}
                >
                  <div className="w-full h-full rounded-full bg-gradient-to-b from-cream/50 to-cream/20" />
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 w-5 h-5 rounded-full bg-cream/40 border border-cream/20" />
                  <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-3 h-4 bg-gold/80 rounded-sm" />
                </div>
              </div>
              <p
                data-testid="turntable-status"
                className={`font-mono text-[10px] uppercase tracking-[0.3em] mt-8 transition-colors duration-500 ${playing ? "text-gold" : "text-cream/40"}`}
              >
                {playing ? "● Now Spinning — Dani Morr Music" : "Paused — Drop the Needle"}
              </p>
            </div>
          </Reveal>

          <Reveal delay={0.2} className="lg:col-span-7">
            <div
              data-testid="playlist-card"
              className="relative bg-tile border border-amber/60 clip-frame-tl p-6 lg:p-8 h-full flex flex-col"
            >
              <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-gold mb-4">
                The Playlist — Stream / Listen
              </p>
              <div id="dm-spotify-embed" data-testid="spotify-playlist-embed" className="w-full min-h-[420px] rounded-xl overflow-hidden" />
              <div className="mt-8 border-t border-cream/10 pt-6 grid sm:grid-cols-3 gap-5">
                {VIBES.map((v, i) => (
                  <div key={i} className="flex items-start gap-3" data-testid={`vibe-note-${i + 1}`}>
                    <Diamond />
                    <p className="text-xs text-cream/55 leading-relaxed">{v}</p>
                  </div>
                ))}
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

export default Music;
